import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Youtube } from 'lucide-react';
import { Input, Segmented } from '@app/components/ui';
import { songsApi } from '@app/api/organization';
import { searchYouTubeVideos, type YouTubeSearchResult } from '@app/services/youtube';
import type { Song } from '@app/types';

interface SongSearchProps {
  churchId: string;
  groupId?: string | null;
  onAddSong: (song: Song) => void;
  onAddYouTube: (result: YouTubeSearchResult) => void;
  onClose: () => void;
}

export const SongSearch: React.FC<SongSearchProps> = ({
  churchId, groupId, onAddSong, onAddYouTube, onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Song[]>([]);
  const [youtubeResults, setYoutubeResults] = useState<YouTubeSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState<'library' | 'youtube'>('library');
  const searchRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const searchSongs = useCallback(async (query: string) => {
    if (searchMode === 'library') {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsSearching(true);
      try {
        const results = await songsApi.getLibrary(churchId, query.trim() || undefined, undefined, 50, { signal: controller.signal }, groupId ?? undefined);
        setSearchResults(results);
      } catch (err: any) {
        if (err.name !== 'AbortError') setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      if (!query.trim() || query.length < 2) {
        setYoutubeResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const results = await searchYouTubeVideos(query, 5);
        setYoutubeResults(results);
      } catch {
        setYoutubeResults([]);
      } finally {
        setIsSearching(false);
      }
    }
  }, [churchId, groupId, searchMode]);

  useEffect(() => {
    const timer = setTimeout(() => searchSongs(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchSongs]);

  // Load all library songs when panel opens or mode switches to library
  useEffect(() => {
    if (searchMode === 'library' && !searchQuery) {
      searchSongs('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMode]);

  const decode = (t: string) => t.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  const switchMode = (m: 'library' | 'youtube') => {
    setSearchMode(m);
    setSearchQuery('');
    setSearchResults([]);
    setYoutubeResults([]);
  };

  return (
    <div className="flex flex-col gap-2 p-2.5 rounded-[16px] bg-fill">
      <div className="flex items-center gap-2">
        <Segmented
          size="sm"
          label="Search in"
          value={searchMode}
          onChange={switchMode}
          options={[
            { value: 'library', label: 'Library' },
            { value: 'youtube', label: 'YouTube' },
          ]}
        />
        <span className="flex-1" />
        <button type="button" onClick={onClose} aria-label="Close song search" className="w-8 h-8 rounded-full text-t4 hover:text-t1 hover:bg-fill2 flex items-center justify-center">
          <X size={15} aria-hidden="true" />
        </button>
      </div>

      <Input
        ref={searchRef}
        height={40}
        pill
        autoFocus
        aria-label={searchMode === 'library' ? 'Search song library' : 'Search YouTube'}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={searchMode === 'library' ? 'Search song library...' : 'Search YouTube for songs...'}
        leading={<Search size={15} />}
        className="!bg-card !text-[13.5px]"
      />

      {searchMode === 'library' && searchResults.length > 0 && (
        <ul className="rounded-[14px] border border-line bg-card max-h-60 overflow-y-auto" aria-label="Library results">
          {searchResults.map((song) => (
            <li key={song.id} className="border-b border-line last:border-0">
              <button type="button" onClick={() => onAddSong(song)} className="w-full text-left px-3.5 py-2.5 hover:bg-fill flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-semibold truncate">{song.title}</span>
                  <span className="block text-[12px] text-t4 truncate">{song.author}</span>
                </span>
                {song.defaultKey && <span className="font-mono text-[12px] font-bold text-acc bg-acc-bg rounded-[8px] px-2 py-1 shrink-0">{song.defaultKey}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {searchMode === 'youtube' && youtubeResults.length > 0 && (
        <ul className="rounded-[14px] border border-line bg-card max-h-56 overflow-y-auto" aria-label="YouTube results">
          {youtubeResults.map((result) => (
            <li key={result.id.videoId} className="border-b border-line last:border-0">
              <button type="button" onClick={() => onAddYouTube(result)} className="w-full text-left px-3 py-2.5 hover:bg-fill flex items-center gap-3">
                <img src={result.snippet.thumbnails.default.url} alt="" className="w-16 h-11 rounded-[8px] object-cover shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-semibold truncate">{decode(result.snippet.title)}</span>
                  <span className="text-[12px] text-t4 flex items-center gap-1">
                    <Youtube size={11} className="text-acc shrink-0" aria-hidden="true" />
                    {result.snippet.channelTitle}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div aria-live="polite" className="text-[12.5px] text-t4 text-center empty:hidden">
        {isSearching
          ? 'Searching…'
          : searchMode === 'library' && searchQuery.length >= 2 && searchResults.length === 0
            ? 'No songs found in your library. Try YouTube.'
            : searchMode === 'youtube' && searchQuery.length >= 2 && youtubeResults.length === 0
              ? 'No YouTube results found.'
              : ''}
      </div>
    </div>
  );
};
